import { useEffect } from 'react';

import { arrayPath } from 'helpers/candidate_application/field-paths';
import { getErrorMessage } from 'helpers/candidate_application/form-page-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import addValidators from 'helpers/validators/apply-validators';
import { useKeyboardHints } from 'hooks/keyboard-hints';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';

import { InputGroup } from '../InputGroup/InputGroup';

// Component to render subField with keyboard hints
const SubFieldWithKeyboardHints = ({
  arrayIndex,
  subField,
  fieldName,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  candidateId,
  handlers,
  getComponentProps,
}) => {
  const keyboardHint = useKeyboardHints(subField?.type);

  // Create validation rules for the subField
  const validationRules = addValidators(
    subField.type,
    subField.min,
    subField.max,
    subField.required,
    subField.validatorName,
    subField.validationParams,
    getValues,
    fieldName,
  );

  return (
    <QuestionWrapper
      key={fieldName}
      fieldName={fieldName}
      question={{ ...subField, text: subField?.title }}
      control={control}
      register={register}
      setValue={setValue}
      trigger={trigger}
      getValues={getValues}
      error={error}
      componentMap={componentMap}
      requirementVariantStyle="text"
      gap="small"
      titleSize="small"
      hideHeader={false}
      keyboardHint={keyboardHint}
      getComponentProps={value => getComponentProps(subField, value, candidateId, handlers, brand)}
      validators={validationRules}
    />
  );
};

export const FixedArray = ({
  question,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  candidateId,
  handlers,
  subFields,
  arrayItemTitles,
  size,
  name,
  validators,
  fieldPathIndex,
  allValues,
  errors,
}) => {
  // Import getComponentProps directly to avoid circular dependency
  const { getComponentProps } = require('helpers/question-config');

  // Use props from QuestionWrapper if available, otherwise fall back to question object
  const arrayName = name || question?.code;
  const subFieldsToUse = subFields || question?.subFields || [];
  const arrayItemTitlesToUse = arrayItemTitles || question?.arrayItemTitles;
  const sizeToUse = size || question?.size || 0;
  const title = question?.title || question?.text;
  const description = question?.description;
  const tooltip = question?.tooltip;

  // Create static array positions based on size
  const staticArrayPositions = [];
  for (let i = 0; i < sizeToUse; i++) {
    staticArrayPositions[i] = i;
  }

  const renderSubField = (subField, arrayIndex) => {
    // Check if subField has conditions
    let conditionsMet = true;
    if (subField?.conditions) {
      conditionsMet = evaluateFieldConditions(subField.conditions, allValues || {}, {
        parentCode: arrayName,
        arrayIndex: arrayIndex,
        fieldPathIndex,
      });
    }

    // Skip rendering if conditions aren't met
    if (!conditionsMet) {
      return null;
    }
    let subFieldData = subField;

    if (subField?.useArrayItemAsDefault) {
      if (Array.isArray(arrayItemTitlesToUse)) {
        const arrayItemTitle = arrayItemTitlesToUse[arrayIndex] || arrayItemTitlesToUse[0];
        const values = Object.values(arrayItemTitle).filter(value => value && value.trim() !== '');
        const defaultValue = values.length > 0 ? values.join(' - ') : arrayItemTitle;
        subFieldData = { ...subField, defaultValue: defaultValue || '' };
      } else {
        const arrayItemTitle = arrayItemTitlesToUse;
        const defaultValue = arrayItemTitle ? arrayItemTitle : '';
        subFieldData = { ...subField, defaultValue: defaultValue };
      }
    }

    const fullFieldName = arrayPath(arrayName, arrayIndex, subField.code);

    return (
      <SubFieldWithKeyboardHints
        key={fullFieldName}
        arrayIndex={arrayIndex}
        fieldName={fullFieldName}
        subField={subFieldData}
        control={control}
        register={register}
        setValue={setValue}
        trigger={trigger}
        getValues={getValues}
        error={getErrorMessage(errors[arrayName]?.[arrayIndex]?.[subFieldData.code])}
        brand={brand}
        candidateId={candidateId}
        componentMap={componentMap}
        handlers={handlers}
        getComponentProps={getComponentProps}
      />
    );
  };

  return (
    <InputGroup>
      <div className="d-flex flex-column u-gap-large">
        {staticArrayPositions.map(arrayIndex => {
          // Get the specific title for this array item
          let itemTitle;
          if (Array.isArray(arrayItemTitlesToUse)) {
            const itemData = arrayItemTitlesToUse[arrayIndex] || arrayItemTitlesToUse[0];

            if (itemData && typeof itemData === 'object') {
              const values = Object.values(itemData).filter(value => value && value.trim() !== '');
              itemTitle = values.length > 0 ? values.join(' - ') : title;
            } else {
              itemTitle = itemData || title;
            }
          } else {
            itemTitle = arrayItemTitlesToUse || title;
          }

          return (
            <InputGroup
              key={`${arrayName}-${arrayIndex}`}
              title={`${arrayIndex + 1}. ${itemTitle}`}>
              <div className="card card-with-border u-padding d-flex flex-column u-gap-large">
                {subFieldsToUse?.map(subField => renderSubField(subField, arrayIndex))}
              </div>
            </InputGroup>
          );
        })}
      </div>
      {error && <span className="error-message">{error}</span>}
    </InputGroup>
  );
};
